/**
 * AuthSession Aggregate Root
 * 
 * Manages user sessions with proper DDD patterns
 * No duplicate entity files!
 */
import { AggregateRootBase } from '../../../../shared/domain/AggregateRootBase';

export interface SessionInvalidated {
  aggregateId: string;
  personId: string;
  reason: string;
  occurredAt: Date;
}

export interface SessionExtended {
  aggregateId: string;
  personId: string;
  extendedBy: number;
  newExpiresAt: Date;
  occurredAt: Date;
}

/**
 * AuthSession Aggregate Root
 * Represents an authenticated user session
 */
export class AuthSession extends AggregateRootBase {
  private constructor(
    id: string,
    private readonly personId: string,
    private readonly email: string,
    private readonly issuedAt: Date,
    private expiresAt: Date,
    private isActive: boolean,
    private readonly ipAddress?: string,
    private readonly userAgent?: string,
    private lastAccessedAt?: Date,
    version: number = 0
  ) {
    super(id, version);
  }

  /**
   * Factory method to create new session
   */
  static create(
    sessionId: string,
    personId: string,
    email: string,
    expirationHours: number,
    ipAddress?: string,
    userAgent?: string
  ): AuthSession {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + expirationHours * 60 * 60 * 1000);

    return new AuthSession(
      sessionId,
      personId,
      email,
      now,
      expiresAt,
      true,
      ipAddress,
      userAgent,
      now
    );
  }

  /**
   * Reconstitute from persistence
   */
  static fromSnapshot(snapshot: any): AuthSession {
    return new AuthSession(
      snapshot.id,
      snapshot.personId,
      snapshot.email,
      snapshot.issuedAt,
      snapshot.expiresAt,
      snapshot.isActive,
      snapshot.ipAddress,
      snapshot.userAgent,
      snapshot.lastAccessedAt,
      snapshot.version
    );
  }

  /**
   * Check if session is valid
   */
  isValid(): boolean {
    if (!this.isActive) return false;
    if (this.expiresAt < new Date()) return false;
    return true;
  }

  /**
   * Invalidate session
   */
  invalidate(reason: string = 'User logout'): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.incrementVersion();

    this.addDomainEvent<SessionInvalidated>({
      aggregateId: this.id,
      personId: this.personId,
      reason,
      occurredAt: new Date()
    });
  }

  /**
   * Extend session expiration
   */
  extend(additionalHours: number): void {
    if (!this.isActive) {
      throw new Error('Cannot extend inactive session');
    }

    const newExpiresAt = new Date(this.expiresAt.getTime() + additionalHours * 60 * 60 * 1000);
    this.expiresAt = newExpiresAt;
    this.incrementVersion();

    this.addDomainEvent<SessionExtended>({
      aggregateId: this.id,
      personId: this.personId,
      extendedBy: additionalHours,
      newExpiresAt,
      occurredAt: new Date()
    });
  }

  /**
   * Update last accessed time
   */
  updateLastAccessed(): void {
    this.lastAccessedAt = new Date();
    this.incrementVersion();
  }

  /**
   * Get remaining time in minutes
   */
  getRemainingMinutes(): number {
    if (!this.isValid()) return 0;
    const remaining = this.expiresAt.getTime() - Date.now();
    return Math.max(0, Math.floor(remaining / (1000 * 60)));
  }

  // Getters for read-only access
  getPersonId(): string { return this.personId; }
  getEmail(): string { return this.email; }
  getExpiresAt(): Date { return this.expiresAt; }
  getIssuedAt(): Date { return this.issuedAt; }
  getIpAddress(): string | undefined { return this.ipAddress; }
  getUserAgent(): string | undefined { return this.userAgent; }

  // Snapshot for persistence
  toSnapshot(): any {
    return {
      id: this.id,
      personId: this.personId,
      email: this.email,
      issuedAt: this.issuedAt,
      expiresAt: this.expiresAt,
      isActive: this.isActive,
      ipAddress: this.ipAddress,
      userAgent: this.userAgent,
      lastAccessedAt: this.lastAccessedAt,
      version: this.version
    };
  }
}